import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyWorkspaceView } from '@futrli/views/views/workspaces/ModifyWorkspaceView/ModifyWorkspaceView';

interface DeleteOrganisationDialogProps {
  workspaceId: string;
  organisationId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteOrganisationDialog = ({
  workspaceId,
  organisationId,
  isOpen,
  onClose
}: DeleteOrganisationDialogProps) => {
  const flags = useFlags();
  const intl = useIntl();

  const view = useApiView(useMemo(() => new ModifyWorkspaceView(flags, workspaceId), [flags, workspaceId]));

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isDeletingOrganisation,
    error: view.state.errorUpdating,
    onAction: () => view.deleteOrganisation(organisationId),
    onClose
  });

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleAction}
      screen={screen}
      title={intl.formatMessage({
        id: 'workspace.delete_organisation.title',
        defaultMessage: 'Are you sure you want to delete this organisation from the workspace?'
      })}
      deleteDescription={intl.formatMessage({
        id: 'workspace.delete_organisation.description',
        defaultMessage:
          'WARNING: This action is irreversible and will remove the organisation from Futrli. All related data including forecasts and reports will be deleted for ALL USERS. I have been given the authority by the Workspace Owner to complete this action and understand that by confirming I am accepting liability for any lost work.'
      })}
      thingsToKnow={[
        intl.formatMessage({
          id: 'workspace.delete_organisation.support',
          defaultMessage: 'If unsure, please reach out to the support team for assistance.'
        })
      ]}
      deleteButtonText={intl.formatMessage({
        id: 'workspace.delete_organisation.confirm',
        defaultMessage: 'Yes, I understand I am deleting the organisation from the workspace!'
      })}
      cancelButtonText={intl.formatMessage({
        id: 'workspace.delete_organisation.cancel',
        defaultMessage: "No, don't remove it"
      })}
      loadingMessage={
        view.state.isLoading
          ? intl.formatMessage({
              id: 'common.loading_please_wait',
              defaultMessage: 'Loading, please wait...'
            })
          : intl.formatMessage({
              id: 'workspace.delete_organisation.deleting',
              defaultMessage: 'Deleting organisation...'
            })
      }
      successMessage={intl.formatMessage({
        id: 'workspace.delete_organisation.deleted',
        defaultMessage: 'Organisation deleted'
      })}
      errorMessage={view.state.errorUpdating?.message}
      dataTestId="delete-organisation-dialog"
    />
  );
};
